import * as React from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import NewReportIcon from 'assets/icons/ic-new-report.svg';
import { Location } from 'types/location.type';
import { useSelector } from 'react-redux';
import {
    getSovLocations,
    getSubmissionDetails,
    getSovLoading,
    getTopLevelUserGroup,
} from 'application/redux/selectors';
import { RouteComponentProps } from 'react-router';
import LumenLoader from 'components/Loader';
import LrStreetView from 'components/LrStreetView';
import { Margin, Spacing } from 'components/Layout';
import LocationAccumulationSummaryTable from 'components/Underwriting/components/LocationAccumulationSummaryTable';
import LocationScratchPad from './components/LocationScratchPad';
import { formatBoolToYesNo, formatDecimal, formatDollar, formatPercent } from 'helpers/formatters';
import { LabeledData } from 'components/Input';
import { getLocationOptionsData } from 'application/redux/selectors/locationOptions';
import MapIcon from '@mui/icons-material/Map';
import { IconButton } from '@mui/material';
import LocationDetailDialog from 'components/LrProducerDashboard/components/LocationMap/components/LocationDetailDialog';
import { MapLocation } from 'types/location.type';
import LocationInspectionList from './components/LocationInspectionList';
import ActivatedTriggerTable from 'components/Underwriting/components/ActivatedTriggerTable';
import { WithStyles } from '@mui/styles';
import styles from './styles';
import { getIsLumenRole } from 'helpers/roles';
import UnderwritingTrendTable from 'components/Underwriting/components/UnderwritingTrendTable';
import { useActions } from 'hooks/useActions';
import * as Actions from 'application/redux/actions';
import { BetterViewSection } from './components/BetterViewSection';
import GoogleResearchSection from './components/GoogleResearchSection';
import { useEdisonDock } from 'hooks/EdisonChatDock';
import { useEffect } from 'react';
import { AiContextTypes } from 'types/ai.types';

const PPAddress: React.FunctionComponent<
    RouteComponentProps<{ id: string; locationId: string }> & WithStyles<typeof styles>
> = (props) => {
    const { classes, match } = props;
    const { locationId } = match.params;
    const { fetchSovReport } = useActions(Actions);

    // Global State
    const submission = useSelector(getSubmissionDetails);
    const sovLocations = useSelector(getSovLocations);
    const sovIsLodaing = useSelector(getSovLoading);
    const locationOptions = useSelector(getLocationOptionsData);
    const group = useSelector(getTopLevelUserGroup);
    const isLumenRole = getIsLumenRole(group);

    // Local State
    const location = sovLocations.find((l) => l.id.toString() === locationId) || ({} as Partial<Location>);
    const [openDetailDialog, setOpenDetailDialog] = React.useState<boolean>(false);

    const { setShowResearchTab, setEntityContext, clearEntityContext } = useEdisonDock();

    useEffect(() => {
        setEntityContext(AiContextTypes.LOCATION, Number(locationId));
        setShowResearchTab(true);
        return () => {
            clearEntityContext();
            setShowResearchTab(false);
        };
    }, []);

    if (!submission || sovIsLodaing) return <LumenLoader />;

    const title = `${location.submissionLocationNumber ? location.submissionLocationNumber + ') ' : ''}${
        location.locationName
    } Location Review${location.totalInsurableValue ? ` (${formatDollar(location.totalInsurableValue, 0)} TIV)` : ''}`;

    const getMapLocation: () => MapLocation = () => {
        const ret: MapLocation = {} as MapLocation;
        ret.TIV = location.totalInsurableValue;
        ret.locationName = location.locationName;
        ret.formattedAddress = location.formattedAddress;
        ret.locationLat = location.locationLat;
        ret.locationLon = location.locationLon;
        ret.locationId = location.id ? String(location.id) : undefined;
        ret.buildingValue = location.buildingValue;
        ret.biRentalIncomeValue = location.biRentalIncomeValue;
        ret.personalPropertyValue = location.personalPropertyValue;
        ret.otherValue = location.otherValue;
        ret.accountName = submission.namedInsured;
        return ret;
    };

    return (
        <>
            <Grid
                container
                className={classes.root}
                direction="column"
                justifyContent="center"
                alignItems="stretch"
                spacing={0}
            >
                <Grid>
                    <Typography variant={'h2'}>
                        {title}
                        <IconButton onClick={() => setOpenDetailDialog(!openDetailDialog)} size="large">
                            <MapIcon color="primary" />
                        </IconButton>
                    </Typography>
                    <Margin left="12">
                        <Typography
                            variant={'subtitle1'}
                        >{`${location.formattedAddress} - ${location.locationCounty}`}</Typography>
                    </Margin>
                </Grid>
                <Grid container xs={11} justifyContent={'space-between'}>
                    <Grid className={classes.margin} style={{ width: '600px' }}>
                        <Typography className={classes.headerLabel}>Accumulations</Typography>
                        <LocationAccumulationSummaryTable
                            submissionId={submission.id}
                            locationIds={[Number(locationId)]}
                        />
                        <Spacing height={'16px'} />
                        <Typography className={classes.headerLabel}>Zone UW History</Typography>
                        <UnderwritingTrendTable submissionId={submission.id} locationId={Number(locationId)} />
                        <Spacing height={'16px'} />
                        <Typography className={classes.headerLabel}>Triggers</Typography>
                        {isLumenRole && <ActivatedTriggerTable locationId={Number(locationId)} />}
                    </Grid>
                    <Grid className={classes.margin} container xs={6} spacing={3}>
                        <Grid>
                            <LabeledData
                                title={'Class'}
                                spacing={0}
                                value={
                                    location.locationClassType
                                        ? locationOptions.LOCATION_CLASSES[location.locationClassType]
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Built'}
                                spacing={0}
                                value={location.yearOfConstruction != null ? location.yearOfConstruction : 'N/A'}
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Const. Type'}
                                spacing={0}
                                value={
                                    location.constructionType
                                        ? locationOptions.LOCATION_CONSTRUCTION_TYPES[location.constructionType]
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Wiring Type'}
                                spacing={0}
                                value={
                                    location.wiringType
                                        ? locationOptions.LOCATION_WIRING_TYPES[location.wiringType]
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'# Buildings'}
                                spacing={0}
                                value={location.numberOfBuildings != null ? location.numberOfBuildings : 'N/A'}
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'# Stories'}
                                spacing={0}
                                value={location.numberOfStories != null ? location.numberOfStories : 'N/A'}
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'# Units'}
                                spacing={0}
                                value={location.numberOfUnits != null ? location.numberOfUnits : 'N/A'}
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Occ %'}
                                spacing={0}
                                value={
                                    location.occupancyPercentage != null
                                        ? formatPercent(location.occupancyPercentage)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'# Pools'}
                                spacing={0}
                                value={location.numberOfPools != null ? location.numberOfPools : 'N/A'}
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Smoke Dets'}
                                spacing={0}
                                value={
                                    location.hwSmokeDetectorAllUnits != null
                                        ? formatBoolToYesNo(location.hwSmokeDetectorAllUnits)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'A/S'}
                                spacing={0}
                                value={
                                    location.automaticSprinklers != null
                                        ? formatBoolToYesNo(location.automaticSprinklers)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'A/S %'}
                                spacing={0}
                                value={
                                    location.automaticSprinklersPercentage != null
                                        ? formatPercent(location.automaticSprinklersPercentage)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Roof Type'}
                                spacing={0}
                                value={
                                    location.roofType ? locationOptions.LOCATION_ROOF_TYPES[location.roofType] : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Roof Covering'}
                                spacing={0}
                                value={
                                    location.roofCoveringType
                                        ? locationOptions.LOCATION_ROOF_COVERING_TYPES[location.roofCoveringType]
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Roof Replaced'}
                                spacing={0}
                                value={
                                    location.lastYearOfRoofReplacement != null
                                        ? location.lastYearOfRoofReplacement
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'% Section 8'}
                                spacing={0}
                                value={
                                    location.percentageSection8 != null
                                        ? formatPercent(location.percentageSection8)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'% Owner Occ'}
                                spacing={0}
                                value={
                                    location.percentageOwnerOccupied != null
                                        ? formatPercent(location.percentageOwnerOccupied)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Owned or Managed'}
                                spacing={0}
                                value={
                                    location.ownershipType
                                        ? locationOptions.LOCATION_OWNERSHIP_TYPES[location.ownershipType]
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'#Years Owned'}
                                spacing={0}
                                value={
                                    location.numberOfYearsOwnedManaged != null
                                        ? location.numberOfYearsOwnedManaged
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Flood Zone'}
                                spacing={0}
                                value={
                                    location.floodZoneType
                                        ? locationOptions.LOCATION_FLOOD_ZONE_TYPES[location.floodZoneType]
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Total Sq Ft'}
                                spacing={0}
                                value={
                                    location.totalSquareFootage != null
                                        ? formatDecimal(location.totalSquareFootage, 0)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Roof Sq Ft'}
                                spacing={0}
                                value={
                                    location.roofSquareFootage != null
                                        ? formatDecimal(location.roofSquareFootage, 0)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'ITV'}
                                spacing={0}
                                value={
                                    location.insuranceToValue != null
                                        ? `${formatDollar(location.insuranceToValue || 0)}`
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Avg Rents'}
                                spacing={0}
                                value={location.averageRents != null ? `${formatDollar(location.averageRents)}` : 'N/A'}
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'TIV/Bldg'}
                                spacing={0}
                                value={
                                    location.tivPerBuilding != null
                                        ? `${formatDollar(location.tivPerBuilding, 0)}`
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Bldg Value'}
                                spacing={0}
                                value={
                                    location.buildingValue != null
                                        ? `${formatDollar(location.buildingValue, 0)}`
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'PP Value'}
                                spacing={0}
                                value={
                                    location.personalPropertyValue != null
                                        ? `${formatDollar(location.personalPropertyValue, 0)}`
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'BI Value'}
                                spacing={0}
                                value={
                                    location.biRentalIncomeValue != null
                                        ? `${formatDollar(location.biRentalIncomeValue, 0)}`
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Other Value'}
                                spacing={0}
                                value={location.otherValue != null ? `${formatDollar(location.otherValue, 0)}` : 'N/A'}
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'BI Ratio'}
                                spacing={0}
                                value={
                                    location.biRentalIncomeValue != null &&
                                    location.buildingValue != null &&
                                    location.buildingValue > 0
                                        ? formatPercent(location.biRentalIncomeValue / location.buildingValue)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Est Roof Cost'}
                                spacing={0}
                                value={location.roofCost != null ? formatDollar(location.roofCost, 0) : 'N/A'}
                            />
                        </Grid>
                        <Grid>
                            <LabeledData
                                title={'Roof Cost %'}
                                spacing={0}
                                value={
                                    location.roofCost != null && location.totalInsurableValue != null
                                        ? formatPercent(location.roofCost / location.totalInsurableValue, 0)
                                        : 'N/A'
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>
                {isLumenRole && (
                    <BetterViewSection
                        location={location}
                        submissionId={submission.id}
                        classes={classes}
                        onRefresh={() => fetchSovReport(submission.id)}
                    />
                )}

                <LrStreetView location={location} />

                <GoogleResearchSection
                    location={location}
                    classes={classes}
                    searchTerms={[
                        { term: 'fire' },
                        { term: 'crime' },
                        { site: 'apartmentratings.com', displayTitle: 'Ratings Research', addressAsCityOnly: true },
                    ]}
                />

                <Spacing height={'24px'} />
                <Grid className={classes.margin} style={{ width: '600px' }}>
                    <Typography className={classes.headerLabel}>Inspections</Typography>
                    <LocationInspectionList
                        locationId={locationId}
                        locationName={location.locationName || ''}
                        locationAddress={location.formattedAddress}
                    />
                </Grid>
            </Grid>

            {!!location && (
                <>
                    <LocationScratchPad
                        submission={submission}
                        locationId={locationId}
                        initialNotes={location.underwritingNote}
                    />
                    <LocationDetailDialog
                        open={openDetailDialog}
                        onClose={() => setOpenDetailDialog(false)}
                        locationData={getMapLocation()}
                    />
                </>
            )}
        </>
    );
};

export default PPAddress;
